import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useCollectionBinding,
  useSlotGroup,
  usePropertyAddressUrl,
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import { btnOnLight } from '../utils/buttonStyles';
import { requestOpenScheduleTourModal } from '../utils/scheduleTourModal';

// ============================================================================
// Schema definitions
// ============================================================================

const tourCtaDef: SlotGroupDefinition = {
  label: 'Schedule Tour CTA',
  fields: {
    label: {
      label: 'Label',
      type: 'text',
      defaultValue: 'SCHEDULE TOUR',
    },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sunday',
};

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function hasValue(v: string | null | undefined): boolean {
  return !!v && v.trim().length > 0;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHourEntry[]): GroupedHours[] {
  const groups: GroupedHours[] = [];
  if (!raw || raw.length === 0) return groups;

  for (const key of DAY_ORDER) {
    const entry: OfficeHourEntry =
      raw.find((e) => hasValue(e.day) && normalizeDay(e.day || '') === key) ?? {};
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed = !hasValue(entry.open_time) || !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

function splitAddressLines(text: string): [string, string | null] {
  const trimmed = text.trim();
  if (!trimmed) return ['', null];
  if (trimmed.includes('\n')) {
    const [first, ...rest] = trimmed.split(/\n+/);
    return [first.trim(), rest.join(', ').trim() || null];
  }
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return [parts[0], parts.slice(1).join(', ')];
  }
  return [trimmed, null];
}

function PhoneIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path
        d="M14.5 9.5c.4-1 1.4-1.6 2.5-1.5l2.2.3c.9.1 1.6.8 1.8 1.7l.7 3.2c.2.8-.2 1.6-.9 2l-1.6.9c1.4 2.8 3.7 5.1 6.5 6.5l.9-1.6c.4-.7 1.2-1.1 2-1l3.2.7c.9.2 1.6.9 1.7 1.8l.3 2.2c.1 1.1-.5 2.1-1.5 2.5-2 .8-4.3.7-6.4-.2-4.3-1.9-7.8-5.4-9.7-9.7-.9-2.1-1-4.4-.2-6.4z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M28 8c1.8 1.2 3.2 2.9 4 4.9M25.5 10.2c1 0.7 1.8 1.7 2.3 2.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <circle cx="20" cy="20" r="11.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M20 13.5V20l4.5 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path
        d="M20 7.5c-4.7 0-8.5 3.8-8.5 8.5 0 6.4 8.5 16.5 8.5 16.5s8.5-10.1 8.5-16.5c0-4.7-3.8-8.5-8.5-8.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="20" cy="16" r="2.75" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function ContactInfoCards() {
  const tourCta = useSlotGroup('contactinfocards.tour_cta', tourCtaDef);
  const phone = useContentToken('contactinfocards.phone') || '';
  const address = useSlotGroup('contactinfocards.address', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const addressHref = address.url || propertyAddressUrl || '';
  const officeHours = useCollectionBinding<OfficeHourEntry>('contactinfocards.office_hours', {
    collection: 'office_hours',
  });

  const phoneDisplay = phone ? formatDisplayPhone(phone) : '(555) 555-5555';
  const addressText = address.text || '123 Main Street, City, ST 00000';
  const [streetLine, cityLine] = splitAddressLines(addressText);
  const groupedHours = groupOfficeHours(officeHours);

  return (
    <section className="cic-section">
      <div className="cic-inner">
        <EditableSlotGroup groupPath="contactinfocards.tour_cta" definition={tourCtaDef}>
          <button
            type="button"
            onClick={requestOpenScheduleTourModal}
            data-vibesite-event="schedule_tour_clicked"
            className={`cic-tour-cta font-body text-sm font-medium tracking-[0.18em] uppercase cursor-pointer ${btnOnLight}`}
          >
            {tourCta.label || 'SCHEDULE TOUR'}
          </button>
        </EditableSlotGroup>

        <div className="cic-cards">
          {/* Call Us */}
          <div className="cic-card">
            <div className="cic-card-icon">
              <PhoneIcon />
            </div>
            <RichTextContent
              path="contactinfocards.call_label"
              as="h3"
              className="cic-card-label"
            />
            {phone ? (
              <a
                href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref cic-card-phone"
              >
                <EditableToken path="contactinfocards.phone" as="span">
                  {phoneDisplay}
                </EditableToken>
              </a>
            ) : (
              <EditableToken path="contactinfocards.phone" as="p" className="cic-card-phone">
                {phoneDisplay}
              </EditableToken>
            )}
          </div>

          {/* Office Hours */}
          <div className="cic-card">
            <div className="cic-card-icon">
              <ClockIcon />
            </div>
            <RichTextContent
              path="contactinfocards.hours_label"
              as="h3"
              className="cic-card-label"
            />
            <EditableCollectionBinding bindingPath="contactinfocards.office_hours" label="Office Hours">
              {groupedHours.length > 0 ? (
                <dl className="cic-hours">
                  {groupedHours.map((group, index) => (
                    <div key={index} className="cic-hours-row">
                      <dt>{group.shortLabel}</dt>
                      <dd>{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="cic-hours-empty">Hours coming soon</p>
              )}
            </EditableCollectionBinding>
          </div>

          {/* Visit Us */}
          <div className="cic-card">
            <div className="cic-card-icon">
              <PinIcon />
            </div>
            <RichTextContent
              path="contactinfocards.visit_label"
              as="h3"
              className="cic-card-label"
            />
            <EditableSlotGroup groupPath="contactinfocards.address" definition={addressLinkDef}>
              {addressHref ? (
                <a
                  href={addressHref}
                  target={address._target || '_blank'}
                  rel="noopener noreferrer"
                  className="cic-card-address"
                >
                  <span className="block">{streetLine}</span>
                  {cityLine ? <span className="block">{cityLine}</span> : null}
                </a>
              ) : (
                <p className="cic-card-address cic-card-address--plain">
                  <span className="block">{streetLine}</span>
                  {cityLine ? <span className="block">{cityLine}</span> : null}
                </p>
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .cic-section {
          position: relative;
          background-color: #f7f6f4;
          background-image:
            radial-gradient(rgba(52, 46, 31, 0.035) 0.6px, transparent 0.6px),
            radial-gradient(rgba(52, 46, 31, 0.025) 0.6px, transparent 0.6px);
          background-size: 3px 3px, 5px 5px;
          background-position: 0 0, 1.5px 1.5px;
          padding: 3rem 1.5rem 3.5rem;
        }
        @media (min-width: 768px) {
          .cic-section {
            padding: 3.5rem 2rem 4rem;
          }
        }

        .cic-inner {
          max-width: 72rem;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2.25rem;
        }

        .cic-tour-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 2.25rem;
          text-decoration: none;
          line-height: 1.2;
          appearance: none;
          font: inherit;
        }

        .cic-cards {
          width: 100%;
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }
        @media (min-width: 768px) {
          .cic-cards {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 1.5rem;
          }
        }

        .cic-card {
          background: #ffffff;
          box-shadow: 0 4px 18px rgba(52, 46, 31, 0.07);
          border-radius: 2px;
          min-height: 220px;
          padding: 2rem 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 0.65rem;
        }

        .cic-card-icon {
          color: #1a1a1a;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.15rem;
        }

        .cic-card-label {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #1a1a1a;
        }

        .cic-card-phone {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          color: #1a1a1a;
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: opacity 0.2s ease;
        }
        a.cic-card-phone:hover {
          opacity: 0.7;
        }

        .cic-hours {
          margin: 0.15rem 0 0;
          width: 100%;
          max-width: 220px;
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }
        .cic-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.45;
          color: #333333;
        }
        .cic-hours-row dt {
          font-weight: 500;
          text-align: left;
        }
        .cic-hours-row dd {
          margin: 0;
          text-align: right;
          white-space: nowrap;
        }
        .cic-hours-empty {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: #888888;
        }

        .cic-card-address {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.45;
          color: #6abada;
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: opacity 0.2s ease;
        }
        a.cic-card-address:hover {
          opacity: 0.75;
        }
        .cic-card-address--plain {
          text-decoration: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactInfoCards',
  displayName: 'Contact Info Cards',
  tokens: {
    'contactinfocards.call_label': {
      label: 'Call Us Label',
      type: 'richtext',
      defaultValue: '<p>CALL US</p>',
    },
    'contactinfocards.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactinfocards.hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>OFFICE HOURS</p>',
    },
    'contactinfocards.visit_label': {
      label: 'Visit Us Label',
      type: 'richtext',
      defaultValue: '<p>VISIT US</p>',
    },
  },
  slotGroups: {
    'contactinfocards.tour_cta': tourCtaDef,
    'contactinfocards.address': addressLinkDef,
  },
  collectionBindings: {
    'contactinfocards.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactInfoCards as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactInfoCards;
